// src/components/ClientOrderForm.jsx
import React, { useState, useMemo } from "react";
import { useNavigationEvent } from "navigation-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShoppingCart,
  faPlus,
  faMinus,
  faTrash,
  faSpinner,
  faCheckCircle,
  faClose,
  faSearch,
  faUser,
  faPhone,
  faEnvelope,
  faMapMarker,
  faNewspaper,
} from "@fortawesome/free-solid-svg-icons";
import useSWR from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });

// Function to get CSRF token
const getCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        method: "GET",
        credentials: "include",
      }
    );

    if (response.ok) {
      const token = await response.text();
      ls.set("usercsrf_token", token);
      return token;
    }

    const storedToken = ls.get("usercsrf_token");
    if (storedToken) {
      return storedToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    return ls.get("usercsrf_token") || null;
  }
};

// Fetcher without Authorization header
const fetcher = async (url) => {
  try {
    const csrfToken = await getCsrfToken();

    const headers = {
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export default function ClientOrderForm() {
  const { stateNavigator } = useNavigationEvent();

  // State
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // SWR Data fetching
  const { data: products, mutate: mutateProducts } = useSWR(
    `${process.env.REACT_APP_PRODUCT_API}?page[limit]=1000&sort=title`,
    fetcher
  );

  const { data: categories } = useSWR(
    `${process.env.REACT_APP_CATEGORY_API}?page[limit]=100`,
    fetcher
  );

  // Helper: Get product stock
  const getProductStock = (product) => {
    const stock = product?.attributes?.field_stock_quantity;
    return typeof stock === 'number' ? stock : parseInt(stock) || 0;
  };

  // Helper: Get product price
  const getProductPrice = (product) => {
    const price = product?.attributes?.field_price;
    return typeof price === 'number' ? price : parseFloat(price) || 0;
  };

  // Helper: Get product image
  const getProductImage = (product) => {
    return product?.attributes?.field_image?.uri?.url || null;
  };

  // Helper: Get product UUID
  const getProductUuid = (product) => {
    return product?.attributes?.uuid || product?.id || null;
  };

  // Helper: Format currency
  const formatCurrency = (amount) => {
    const num = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
    return `GH₵${num.toFixed(2)}`;
  };

  // Calculate cart totals
  const cartTotals = useMemo(() => {
    const subtotal = cart.reduce((sum, item) => {
      const price = getProductPrice(item);
      return sum + (price * (item.quantity || 0));
    }, 0);
    
    const totalItems = cart.reduce((sum, item) => sum + (item.quantity || 0), 0);
    
    return { subtotal, totalItems };
  }, [cart]);

  // Filter products
  const filteredProducts = useMemo(() => {
    if (!products?.data) return [];

    return products.data.filter((product) => {
      const title = product?.attributes?.title || "";
      const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase());
      
      const categoryId = product?.relationships?.field_category?.data?.id;
      const matchesCategory = selectedCategory === "all" || categoryId === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, selectedCategory]);

  // Add to cart
  const addToCart = (product) => {
    const stock = getProductStock(product);
    if (stock <= 0) {
      setErrorMessage("This product is out of stock");
      setModalError(true);
      return;
    }

    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        if ((existingItem.quantity || 0) + 1 > stock) {
          setErrorMessage(`Only ${stock} units available in stock`);
          setModalError(true);
          return prevCart;
        }
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: (item.quantity || 0) + 1 }
            : item
        );
      }
      return [...prevCart, { 
        ...product, 
        quantity: 1,
        maxStock: stock
      }];
    });
  };

  // Remove from cart
  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  // Update quantity
  const updateQuantity = (productId, newQuantity) => {
    const product = products?.data?.find(p => p.id === productId);
    if (!product) return;

    const stock = getProductStock(product);
    
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }

    if (newQuantity > stock) {
      setErrorMessage(`Only ${stock} units available in stock`);
      setModalError(true);
      return;
    }

    setCart(prevCart =>
      prevCart.map(item =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!customerInfo.name.trim()) {
      errors.name = "Name is required";
    }
    if (!customerInfo.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(customerInfo.email)) {
      errors.email = "Email is invalid";
    }
    if (!customerInfo.phone.trim()) {
      errors.phone = "Phone number is required";
    }
    if (cart.length === 0) {
      errors.cart = "Please add at least one item to your order";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Find or create customer - Cleaned version
  const findOrCreateCustomer = async () => {
    try {
      const csrfToken = await getCsrfToken();

      // First, try to find existing customer by email
      const searchUrl = `${process.env.REACT_APP_CUSTOMER_API}?filter[field_customer_email][value]=${encodeURIComponent(customerInfo.email)}`;
      
      const searchResponse = await fetch(searchUrl, {
        method: 'GET',
        credentials: "include",
        headers: {
          Accept: "application/vnd.api+json",
          "Content-Type": "application/vnd.api+json",
          ...(csrfToken && { "X-CSRF-Token": csrfToken }),
        },
      });

      if (searchResponse.ok) {
        const data = await searchResponse.json();
        
        if (data.data && data.data.length > 0) {
          // Try different ways to get the UUID
          let uuid = null;
          
          // Method 1: From id (Drupal JSON:API uses id as UUID)
          if (data.data[0].id && data.data[0].id.length === 36) {
            uuid = data.data[0].id;
          }
          // Method 2: From attributes.uuid
          else if (data.data[0].attributes && data.data[0].attributes.uuid) {
            uuid = data.data[0].attributes.uuid;
          }
          
          if (uuid) {
            return uuid;
          }
        }
      }

      // If not found, create new customer
      const createData = {
        data: {
          type: "node--customer",
          attributes: {
            title: customerInfo.name,
            field_customer_email: customerInfo.email,
            field_phone_number: customerInfo.phone,
            field_customer_address: customerInfo.address || "",
          },
        },
      };

      const createResponse = await fetch(
        process.env.REACT_APP_CUSTOMER_API,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            ...(csrfToken && { "X-CSRF-Token": csrfToken }),
          },
          body: JSON.stringify(createData),
        }
      );

      if (createResponse.ok) {
        const data = await createResponse.json();
        
        // Extract UUID from the response
        let uuid = null;
        if (data.data?.id && data.data.id.length === 36) {
          uuid = data.data.id;
        } else if (data.data?.attributes?.uuid) {
          uuid = data.data.attributes.uuid;
        }
        
        if (uuid) {
          return uuid;
        } else {
          throw new Error("Could not extract UUID from created customer");
        }
      } else {
        const errorText = await createResponse.text();
        throw new Error(`Failed to create customer: ${errorText}`);
      }
    } catch (error) {
      console.error("Customer creation error:", error);
      throw new Error("Failed to create or find customer. Please try again.");
    }
  };

  // Submit order
  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Find or create customer
      const customerUuid = await findOrCreateCustomer();
      
      if (!customerUuid) {
        throw new Error("Could not find or create customer");
      }
      
      // Prepare order data
      const orderData = {
        order_number: `ONL-${Date.now()}`,
        customer_nid: customerUuid,
        cashier_uid: 1,
        items: cart.map(item => ({
          product_uuid: getProductUuid(item),
          unit_price: getProductPrice(item),
          qty: item.quantity || 0,
        })),
        notes: customerInfo.notes || "Online order",
        payment_type: "online",
        payment_method: "cash_on_delivery",
        amount_paid: 0,
        discount: 0,
        order_status: "pending",
        delivery_address: customerInfo.address || "",
      };

      // Get CSRF token
      const csrfToken = await getCsrfToken();

      // Create order
      const orderResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/pos/checkout`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(csrfToken && { "X-CSRF-Token": csrfToken }),
          },
          body: JSON.stringify(orderData),
        }
      );

      if (orderResponse.ok) {
        const result = await orderResponse.json();
        
        if (result.status === "success") {
          setSuccess(true);
          setOrderDetails(result);
          setCart([]);
          setCustomerInfo({
            name: "",
            email: "",
            phone: "",
            address: "",
            notes: "",
          });
          setModalSuccessMsg(true);
        } else {
          throw new Error(result.message || "Failed to create order");
        }
      } else {
        const errorText = await orderResponse.text();
        throw new Error(`Failed to create order. Please try again.`);
      }
    } catch (err) {
      console.error("Order submission error:", err);
      setErrorMessage(err.message || "Failed to submit order. Please try again.");
      setModalError(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Clear cart
  const clearCart = () => {
    if (cart.length > 0 && window.confirm("Are you sure you want to clear your cart?")) {
      setCart([]);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Reset form
  const resetForm = () => {
    setSuccess(false);
    setOrderDetails(null);
    setError(null);
  };

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="client-order-container" style={styles.container}>
          <TopActionMenus
            AddTitle="Online Order Form"
            buttonLabel="Back to Orders"
            onClick={() => stateNavigator.navigate("onlineorders")}
          />

          {success && orderDetails ? (
            // Success Message
            <div style={styles.successCard}>
              <div style={styles.successIcon}>
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <h2 style={styles.successTitle}>Order Placed Successfully!</h2>
              <p style={styles.successText}>
                Your order has been received and will be processed shortly.
              </p>
              <div style={styles.orderDetailsBox}>
                <div style={styles.orderDetailRow}>
                  <span style={styles.orderDetailLabel}>Order Number:</span>
                  <span style={styles.orderDetailValue}>{orderDetails.order_number}</span>
                </div>
                <div style={styles.orderDetailRow}>
                  <span style={styles.orderDetailLabel}>Order UUID:</span>
                  <span style={styles.orderDetailValue}>{orderDetails.order_uuid}</span>
                </div>
                <div style={styles.orderDetailRow}>
                  <span style={styles.orderDetailLabel}>Total Amount:</span>
                  <span style={styles.orderDetailValue}>
                    {formatCurrency(orderDetails.order_summary?.total_amount || 0)}
                  </span>
                </div>
                <div style={styles.orderDetailRow}>
                  <span style={styles.orderDetailLabel}>Status:</span>
                  <span style={styles.orderDetailValue}>Pending</span>
                </div>
              </div>
              <p style={styles.successNote}>
                We will contact you shortly to confirm your order.
              </p>
              <button
                style={styles.newOrderButton}
                onClick={resetForm}
              >
                Place Another Order
              </button>
            </div>
          ) : (
            // Main Order Form
            <div style={styles.orderContent}>
              {/* Product Catalog Section */}
              <div style={styles.catalogSection}>
                <div style={styles.catalogInnerWrapper}>
                  <div style={styles.catalogHeader}>
                    <h2 style={styles.sectionTitle}>Products</h2>
                    <div style={styles.catalogControls}>
                      <div style={styles.searchBox}>
                        <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} />
                        <input
                          type="text"
                          placeholder="Search products..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={styles.searchInput}
                        />
                      </div>
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        style={styles.categoryFilter}
                      >
                        <option value="all">All Categories</option>
                        {categories?.data?.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.attributes.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {!products ? (
                    <div style={styles.loadingContainer}>
                      <FontAwesomeIcon icon={faSpinner} spin style={styles.loadingIcon} />
                      <span>Loading products...</span>
                    </div>
                  ) : (
                    <div style={styles.productGrid}>
                      {filteredProducts.length === 0 ? (
                        <div style={styles.noProducts}>
                          {searchTerm ? "No products match your search" : "No products available"}
                        </div>
                      ) : (
                        filteredProducts.map((product) => {
                          const stock = getProductStock(product);
                          const price = getProductPrice(product);
                          const inCart = cart.find(item => item.id === product.id);
                          const image = getProductImage(product);
                          
                          return (
                            <div key={product.id} style={styles.productCard}>
                              <div style={styles.productImage}>
                                {image ? (
                                  <img src={image} alt={product.attributes.title} style={styles.productImg} />
                                ) : (
                                  <div style={styles.noImage}>No Image</div>
                                )}
                                {stock <= 0 && (
                                  <div style={styles.outOfStockBadge}>Out of Stock</div>
                                )}
                              </div>
                              <div style={styles.productInfo}>
                                <h3 style={styles.productTitle}>{product.attributes.title}</h3>
                                <p style={styles.productPrice}>{formatCurrency(price)}</p>
                                <p style={{
                                  ...styles.productStock,
                                  color: stock > 0 ? "#388e3c" : "#d32f2f"
                                }}>
                                  {stock > 0 ? `${stock} in stock` : "Out of Stock"}
                                </p>
                                <button
                                  onClick={() => addToCart(product)}
                                  disabled={stock <= 0 || (inCart && (inCart.quantity || 0) >= stock)}
                                  style={{
                                    ...styles.addToCartBtn,
                                    ...((stock <= 0 || (inCart && (inCart.quantity || 0) >= stock)) 
                                      ? styles.addToCartBtnDisabled : {})
                                  }}
                                >
                                  {inCart ? `In Cart (${inCart.quantity || 0})` : "Add to Cart"}
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Cart Section */}
              <div style={styles.cartSection}>
                <div style={styles.cartContainer}>
                  <div style={styles.cartHeader}>
                    <h2 style={styles.sectionTitle}>
                      <FontAwesomeIcon icon={faShoppingCart} style={styles.cartIcon} />
                      Your Cart
                    </h2>
                    {cart.length > 0 && (
                      <button
                        onClick={clearCart}
                        style={styles.clearCartBtn}
                      >
                        <FontAwesomeIcon icon={faTrash} /> Clear
                      </button>
                    )}
                  </div>

                  {cart.length === 0 ? (
                    <div style={styles.emptyCart}>
                      <p style={styles.emptyCartText}>Your cart is empty</p>
                      <p style={styles.emptyCartHint}>Browse products and add them to your cart</p>
                    </div>
                  ) : (
                    <>
                      <div style={styles.cartItems}>
                        {cart.map((item) => {
                          const price = getProductPrice(item);
                          const quantity = item.quantity || 0;
                          return (
                            <div key={item.id} style={styles.cartItem}>
                              <div style={styles.cartItemInfo}>
                                <span style={styles.cartItemName}>{item.attributes.title}</span>
                                <span style={styles.cartItemPrice}>
                                  {formatCurrency(price * quantity)}
                                </span>
                              </div>
                              <div style={styles.cartItemControls}>
                                <div style={styles.quantityControl}>
                                  <button
                                    onClick={() => updateQuantity(item.id, quantity - 1)}
                                    style={styles.qtyBtn}
                                  >
                                    <FontAwesomeIcon icon={faMinus} />
                                  </button>
                                  <span style={styles.qtyDisplay}>{quantity}</span>
                                  <button
                                    onClick={() => updateQuantity(item.id, quantity + 1)}
                                    style={{
                                      ...styles.qtyBtn,
                                      ...(quantity >= (item.maxStock || 0) ? styles.qtyBtnDisabled : {})
                                    }}
                                    disabled={quantity >= (item.maxStock || 0)}
                                  >
                                    <FontAwesomeIcon icon={faPlus} />
                                  </button>
                                </div>
                                <button
                                  onClick={() => removeFromCart(item.id)}
                                  style={styles.removeBtn}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div style={styles.cartSummary}>
                        <div style={styles.cartTotal}>
                          <span style={styles.cartTotalLabel}>Total Items:</span>
                          <span style={styles.cartTotalValue}>{cartTotals.totalItems}</span>
                        </div>
                        <div style={styles.cartTotal}>
                          <span style={styles.cartTotalLabel}>Subtotal:</span>
                          <span style={styles.cartTotalValue}>{formatCurrency(cartTotals.subtotal)}</span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Customer Information Form */}
                  {cart.length > 0 && (
                    <div style={styles.customerForm}>
                      <h3 style={styles.formTitle}>Customer Information</h3>
                      <form onSubmit={handleSubmitOrder}>
                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>
                            <FontAwesomeIcon icon={faUser} style={styles.formIcon} />
                            Full Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={customerInfo.name}
                            onChange={handleInputChange}
                            style={{
                              ...styles.formInput,
                              ...(formErrors.name ? styles.formInputError : {})
                            }}
                            placeholder="Enter your full name"
                          />
                          {formErrors.name && (
                            <span style={styles.formError}>{formErrors.name}</span>
                          )}
                        </div>

                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>
                            <FontAwesomeIcon icon={faEnvelope} style={styles.formIcon} />
                            Email Address *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={customerInfo.email}
                            onChange={handleInputChange}
                            style={{
                              ...styles.formInput,
                              ...(formErrors.email ? styles.formInputError : {})
                            }}
                            placeholder="Enter your email"
                          />
                          {formErrors.email && (
                            <span style={styles.formError}>{formErrors.email}</span>
                          )}
                        </div>

                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>
                            <FontAwesomeIcon icon={faPhone} style={styles.formIcon} />
                            Phone Number *
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={customerInfo.phone}
                            onChange={handleInputChange}
                            style={{
                              ...styles.formInput,
                              ...(formErrors.phone ? styles.formInputError : {})
                            }}
                            placeholder="Enter your phone number"
                          />
                          {formErrors.phone && (
                            <span style={styles.formError}>{formErrors.phone}</span>
                          )}
                        </div>

                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>
                            <FontAwesomeIcon icon={faMapMarker} style={styles.formIcon} />
                            Delivery Address
                          </label>
                          <input
                            type="text"
                            name="address"
                            value={customerInfo.address}
                            onChange={handleInputChange}
                            style={styles.formInput}
                            placeholder="Enter your delivery address"
                          />
                        </div>

                        <div style={styles.formGroup}>
                          <label style={styles.formLabel}>
                            <FontAwesomeIcon icon={faNewspaper} style={styles.formIcon} />
                            Order Notes
                          </label>
                          <textarea
                            name="notes"
                            value={customerInfo.notes}
                            onChange={handleInputChange}
                            style={styles.formTextarea}
                            placeholder="Special instructions or delivery notes"
                            rows="3"
                          />
                        </div>

                        <button
                          type="submit"
                          disabled={submitting || cart.length === 0}
                          style={{
                            ...styles.submitBtn,
                            ...(submitting || cart.length === 0 ? styles.submitBtnDisabled : {})
                          }}
                        >
                          {submitting ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin style={styles.spinnerIcon} />
                              Placing Order...
                            </>
                          ) : (
                            "Place Order"
                          )}
                        </button>
                      </form>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <DashboardContentFooter />

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message="Order placed successfully!"
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faClose}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

// Styles remain the same...
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ecf2fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  successCard: {
    background: "white",
    padding: "40px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    textAlign: "center",
    maxWidth: "600px",
    margin: "40px auto",
  },
  successIcon: {
    fontSize: "64px",
    color: "#28a745",
    marginBottom: "20px",
  },
  successTitle: {
    color: "#333",
    marginBottom: "10px",
  },
  successText: {
    color: "#666",
    marginBottom: "20px",
    fontSize: "16px",
  },
  orderDetailsBox: {
    background: "#f8f9fa",
    padding: "20px",
    borderRadius: "8px",
    textAlign: "left",
    marginBottom: "20px",
  },
  orderDetailRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: "1px solid #e0e0e0",
  },
  orderDetailLabel: {
    fontWeight: "600",
    color: "#555",
  },
  orderDetailValue: {
    color: "#333",
  },
  successNote: {
    color: "#666",
    marginBottom: "20px",
  },
  newOrderButton: {
    background: "#007bff",
    color: "white",
    border: "none",
    padding: "12px 32px",
    borderRadius: "6px",
    fontSize: "16px",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  orderContent: {
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr",
    gap: "24px",
    marginTop: "20px",
  },
  catalogSection: {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  catalogInnerWrapper: {
    margin: "0 auto",
  },
  catalogHeader: {
    marginBottom: "20px",
  },
  sectionTitle: {
    margin: "0 0 15px 0",
    color: "#333",
    fontSize: "20px",
  },
  catalogControls: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  searchBox: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "0 12px",
    background: "white",
    minWidth: "200px",
  },
  searchIcon: {
    color: "#999",
    marginRight: "8px",
  },
  searchInput: {
    border: "none",
    padding: "10px 0",
    fontSize: "14px",
    width: "100%",
    outline: "none",
  },
  categoryFilter: {
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    background: "white",
    cursor: "pointer",
    minWidth: "150px",
  },
  loadingContainer: {
    textAlign: "center",
    padding: "40px",
    color: "#666",
  },
  loadingIcon: {
    marginRight: "10px",
  },
  productGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
  },
  noProducts: {
    gridColumn: "1 / -1",
    textAlign: "center",
    padding: "40px",
    color: "#666",
  },
  productCard: {
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    overflow: "hidden",
    transition: "transform 0.2s, box-shadow 0.2s",
    display: "flex",
    flexDirection: "column",
  },
  productImage: {
    height: "160px",
    background: "#f8f9fa",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
  },
  productImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  noImage: {
    color: "#999",
    fontSize: "14px",
  },
  outOfStockBadge: {
    position: "absolute",
    top: "10px",
    right: "10px",
    background: "#d32f2f",
    color: "white",
    padding: "4px 12px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "600",
  },
  productInfo: {
    padding: "12px",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  productTitle: {
    margin: "0 0 6px 0",
    fontSize: "14px",
    color: "#333",
    fontWeight: "600",
    lineHeight: "1.3",
    minHeight: "36px",
  },
  productPrice: {
    fontSize: "16px",
    fontWeight: "bold",
    color: "#28a745",
    margin: "0 0 4px 0",
  },
  productStock: {
    fontSize: "12px",
    margin: "0 0 8px 0",
  },
  addToCartBtn: {
    width: "100%",
    padding: "8px",
    background: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontSize: "13px",
    cursor: "pointer",
    transition: "background 0.2s",
    marginTop: "auto",
  },
  addToCartBtnDisabled: {
    background: "#6c757d",
    cursor: "not-allowed",
    opacity: 0.6,
  },
  cartSection: {
    flex: 1,
    position: "sticky",
    top: "20px",
    alignSelf: "start",
  },
  cartContainer: {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    maxHeight: "calc(100vh - 200px)",
    display: "flex",
    flexDirection: "column",
    minWidth: "320px",
  },
  cartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
    paddingBottom: "15px",
    borderBottom: "2px solid #f0f0f0",
  },
  cartIcon: {
    marginRight: "10px",
    color: "#007bff",
  },
  clearCartBtn: {
    background: "none",
    border: "none",
    color: "#dc3545",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  emptyCart: {
    textAlign: "center",
    padding: "40px 20px",
  },
  emptyCartText: {
    color: "#666",
    fontSize: "16px",
    margin: "0 0 5px 0",
  },
  emptyCartHint: {
    color: "#999",
    fontSize: "14px",
    margin: 0,
  },
  cartItems: {
    flex: 1,
    overflowY: "auto",
    marginBottom: "15px",
  },
  cartItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0",
    borderBottom: "1px solid #f0f0f0",
  },
  cartItemInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  cartItemName: {
    fontSize: "14px",
    color: "#333",
  },
  cartItemPrice: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#28a745",
  },
  cartItemControls: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  quantityControl: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  qtyBtn: {
    width: "28px",
    height: "28px",
    border: "1px solid #ddd",
    background: "white",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
  },
  qtyBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  qtyDisplay: {
    minWidth: "24px",
    textAlign: "center",
    fontSize: "14px",
    fontWeight: "600",
  },
  removeBtn: {
    background: "none",
    border: "none",
    color: "#dc3545",
    cursor: "pointer",
    fontSize: "14px",
  },
  cartSummary: {
    borderTop: "2px solid #f0f0f0",
    paddingTop: "15px",
    marginBottom: "15px",
  },
  cartTotal: {
    display: "flex",
    justifyContent: "space-between",
    padding: "5px 0",
    fontSize: "16px",
  },
  cartTotalLabel: {
    color: "#555",
  },
  cartTotalValue: {
    fontWeight: "bold",
    color: "#333",
  },
  customerForm: {
    borderTop: "2px solid #f0f0f0",
    paddingTop: "20px",
    marginTop: "10px",
    overflowY: "auto",
  },
  formTitle: {
    margin: "0 0 15px 0",
    fontSize: "18px",
    color: "#333",
  },
  formGroup: {
    marginBottom: "15px",
  },
  formLabel: {
    display: "block",
    marginBottom: "5px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#555",
  },
  formIcon: {
    marginRight: "5px",
    color: "#007bff",
  },
  formInput: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    transition: "border-color 0.2s",
    outline: "none",
    boxSizing: "border-box",
  },
  formInputError: {
    borderColor: "#dc3545",
  },
  formTextarea: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    resize: "vertical",
  },
  formError: {
    color: "#dc3545",
    fontSize: "12px",
    marginTop: "5px",
    display: "block",
  },
  submitBtn: {
    width: "100%",
    padding: "14px",
    background: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  },
  submitBtnDisabled: {
    background: "#6c757d",
    cursor: "not-allowed",
    opacity: 0.6,
  },
  spinnerIcon: {
    animation: "spin 1s linear infinite",
  },
};

// Responsive Styles
const responsiveStyles = document.createElement("style");
responsiveStyles.textContent = `
  @media (max-width: 1200px) {
    .client-order-container .orderContent {
      grid-template-columns: 1.5fr 1fr;
    }
    .client-order-container .catalogInnerWrapper {
      max-width: 100%;
    }
  }

  @media (max-width: 1024px) {
    .client-order-container .orderContent {
      grid-template-columns: 1fr;
    }
    .client-order-container .cartSection {
      position: static;
    }
    .client-order-container .cartContainer {
      max-height: none;
      min-width: auto;
    }
    .client-order-container .productGrid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @media (max-width: 768px) {
    .client-order-container .productGrid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 600px) {
    .client-order-container {
      padding: 16px;
    }
    .client-order-container .catalogControls {
      flex-direction: column;
    }
    .client-order-container .productGrid {
      grid-template-columns: 1fr;
    }
    .client-order-container .productImage {
      height: 200px;
    }
    .client-order-container .orderDetailsBox {
      font-size: 14px;
    }
    .client-order-container .productTitle {
      font-size: 14px;
      min-height: 35px;
    }
  }

  @media (max-width: 400px) {
    .client-order-container .productImage {
      height: 160px;
    }
    .client-order-container .productInfo {
      padding: 12px;
    }
    .client-order-container .cartContainer {
      padding: 15px;
    }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(responsiveStyles);